import { asHTML } from '@prismicio/client'
import { FormSlice } from '../../prismicio-types'
import linkResolver from '../linkResolver'
import styles from './Form.module.css'
import mainStyles from '../Main.module.css'
import { ShortLang } from '@/const'
import { dictionary } from '@/dictionary'

const FORMSPREE_URL = 'https://formspree.io/f/xppqebvw'

const fields = [
	{ name: 'prename', type: 'text', autocomplete: 'given-name' },
	{ name: 'name', type: 'text', autocomplete: 'family-name' },
	{ name: 'email', type: 'email', autocomplete: 'email' },
	{ name: 'address', type: 'text', autocomplete: 'street-address' },
	{ name: 'zip', type: 'text', autocomplete: 'postal-code' },
	{ name: 'city', type: 'text', autocomplete: 'address-level2' },
	{ name: 'country', type: 'text', autocomplete: 'country-name' }
] as const

const memberships = [
	{ id: 'individual', price: 'CHF 95.–' },
	{ id: 'family', price: 'CHF 195.–' },
	{ id: 'bringAFriend', price: 'CHF 165.–' },
	{ id: 'corporate', price: 'CHF 395.–' }
] as const

export class Form {
	private readonly form: HTMLFormElement
	private readonly button: HTMLButtonElement
	private readonly status: HTMLElement
	private readonly success: HTMLElement

	constructor(private readonly node: HTMLElement) {
		this.onSubmit = this.onSubmit.bind(this)
		this.form = node.querySelector('form')!
		this.button = node.querySelector(`.${styles.Submit}`)!
		this.status = node.querySelector(`.${styles.Status}`)!
		this.success = node.querySelector(`.${styles.Success}`)!
		this.form.addEventListener('submit', this.onSubmit)
	}

	async onSubmit(event: SubmitEvent) {
		event.preventDefault()

		this.button.disabled = true
		this.status.textContent = ''

		try {
			const response = await fetch(FORMSPREE_URL, {
				method: 'POST',
				body: new FormData(this.form),
				headers: { Accept: 'application/json' }
			})

			if (!response.ok) {
				throw new Error(`Formspree responded with ${response.status}`)
			}

			this.form.reset()
			this.node.classList.add(styles.IsSent)
			document.body.classList.add(mainStyles.LilacDream)
			this.success.focus({ preventScroll: true })
			this.success.scrollIntoView({ behavior: 'smooth', block: 'center' })
		} catch (error) {
			console.error(error)
			this.status.textContent = this.form.dataset.error ?? ''
		} finally {
			this.button.disabled = false
		}
	}

	dispose() {
		this.form.removeEventListener('submit', this.onSubmit)
	}

	static render(props: FormSlice, lang: ShortLang = 'de') {
		const {
			primary: { success_message }
		} = props
		const t = dictionary[lang].form

		return /* HTML */ `
			<div class="${styles.Main}">
				<form class="${styles.Form}" action="${FORMSPREE_URL}" method="POST" data-error="${t.error}">
					${fields
						.map(
							({ name, type, autocomplete }) => /* HTML */ `
								<input
									class="${styles.Input} ${styles[name] ?? ''}"
									type="${type}"
									name="${name}"
									placeholder="${t[name]} *"
									aria-label="${t[name]}"
									autocomplete="${autocomplete}"
									required
								/>
							`
						)
						.join('')}
					<fieldset class="${styles.Memberships}">
						<legend class="${styles.Legend}">${t.membershipLegend}</legend>
						${memberships
							.map(
								({ id, price }, index) => /* HTML */ `
									<label class="${styles.Membership}">
										<input
											class="${styles.Radio}"
											type="radio"
											name="membership"
											value="${t.memberships[id]}"
											${index === 0 ? 'required checked' : ''}
										/>
										<span class="${styles.MembershipLabel}">${t.memberships[id]}</span>
										<span class="${styles.MembershipPrice}">${price}</span>
										<span class="${styles.MembershipDescription}">${t.perYear}</span>
									</label>
								`
							)
							.join('')}
					</fieldset>
					<button class="${styles.Submit}" type="submit">${t.submit}</button>
					<p class="${styles.Status}" role="status" aria-live="polite"></p>
				</form>
				<div class="${styles.Success}" tabindex="-1">${asHTML(success_message, { linkResolver })}</div>
			</div>
		`
	}
}
